import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Divider,
  CircularProgress,
} from '@mui/material';
import { ChangePasswordData } from '../../types/user';
import { profileService } from '../../services/profileService';

interface PasswordChangeFormProps {
  onPasswordChanged?: () => void;
}

export const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({ onPasswordChanged }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = useForm<ChangePasswordData>();

  const watchNewPassword = watch('new_password');

  const onSubmit = async (data: ChangePasswordData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await profileService.changePassword(data);
      setSuccess('Đổi mật khẩu thành công! Vui lòng đăng nhập lại.');
      reset();
      if (onPasswordChanged) {
        onPasswordChanged();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra khi đổi mật khẩu');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Đổi mật khẩu
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Mật khẩu hiện tại *"
              type="password"
              {...register('current_password', {
                required: 'Vui lòng nhập mật khẩu hiện tại'
              })}
              error={!!errors.current_password}
              helperText={errors.current_password?.message}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Mật khẩu mới *"
              type="password"
              {...register('new_password', {
                required: 'Vui lòng nhập mật khẩu mới',
                minLength: {
                  value: 8,
                  message: 'Mật khẩu phải có ít nhất 8 ký tự'
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Mật khẩu phải có ít nhất 1 chữ hoa, 1 chữ thường và 1 số'
                }
              })}
              error={!!errors.new_password}
              helperText={errors.new_password?.message}
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Xác nhận mật khẩu mới *"
              type="password"
              {...register('new_password_confirmation', {
                required: 'Vui lòng xác nhận mật khẩu mới',
                validate: (value) => value === watchNewPassword || 'Mật khẩu xác nhận không khớp'
              })}
              error={!!errors.new_password_confirmation}
              helperText={errors.new_password_confirmation?.message}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => reset()}
              disabled={isLoading}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : null}
            >
              {isLoading ? 'Đang đổi...' : 'Đổi mật khẩu'}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}; 